import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import { Checkbox } from '@atlaskit/checkbox/checkbox';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import {
	getSuggestionLogoKeyFromContentUrl,
	getSuggestionProductKeyFromContentUrl,
	type Suggestion,
} from './__types__/RemoteObject.ts';
import { ProductLogo } from './ProductLogos.tsx';

export type SuggestionDecisionTrigger = 'addToSpace' | 'closeWithRejections';

export type SuggestionDecision = {
	accepted: Suggestion[];
	rejected: Suggestion[];
	trigger: SuggestionDecisionTrigger;
};

type DecisionType = 'accepted' | 'rejected';

const i18n = defineMessages({
	title: {
		id: 'space-curation.suggestion-decision-feedback-modal.title',
		defaultMessage: 'Help us improve suggestions',
		description:
			'Title of the optional survey modal shown after a user adds or rejects suggested content in a space',
	},
	description: {
		id: 'space-curation.suggestion-decision-feedback-modal.description',
		defaultMessage: 'Tell us why you made these choices. Every question is optional.',
		description:
			'Subtitle of the suggestion feedback survey modal explaining that the user can pick reasons for each decision',
	},
	acceptedHeading: {
		id: 'space-curation.suggestion-decision-feedback-modal.accepted-heading',
		defaultMessage: 'Added to space',
		description:
			'Heading above the list of suggestions the user just added to the space, in the feedback survey modal',
	},
	rejectedHeading: {
		id: 'space-curation.suggestion-decision-feedback-modal.rejected-heading',
		defaultMessage: 'Rejected',
		description:
			'Heading above the list of suggestions the user just rejected, in the feedback survey modal',
	},
	acceptedGroupLabel: {
		id: 'space-curation.suggestion-decision-feedback-modal.accepted-group-label',
		defaultMessage: 'Why did you add {title}?',
		description:
			'Accessible label for the group of reason checkboxes on a suggestion the user added. {title} is the suggested item title',
	},
	rejectedGroupLabel: {
		id: 'space-curation.suggestion-decision-feedback-modal.rejected-group-label',
		defaultMessage: 'Why did you reject {title}?',
		description:
			'Accessible label for the group of reason checkboxes on a suggestion the user rejected. {title} is the suggested item title',
	},
	reasonRelevant: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.relevant',
		defaultMessage: 'Relevant to this space',
		description: 'Reason checkbox for an added suggestion: the content belongs to the space topic',
	},
	reasonUseful: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.useful',
		defaultMessage: 'Useful for my team',
		description: 'Reason checkbox for an added suggestion: the content helps the team',
	},
	reasonGoodLocation: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.good-location',
		defaultMessage: 'Suggested location was right',
		description:
			'Reason checkbox for an added suggestion: the folder or page it was suggested under was correct',
	},
	reasonUpToDate: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.up-to-date',
		defaultMessage: 'Up to date',
		description: 'Reason checkbox for an added suggestion: the content is current',
	},
	reasonNotRelevant: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.not-relevant',
		defaultMessage: 'Not relevant to this space',
		description:
			'Reason checkbox for a rejected suggestion: the content does not belong to the space topic',
	},
	reasonWrongLocation: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.wrong-location',
		defaultMessage: 'Wrong location',
		description:
			'Reason checkbox for a rejected suggestion: the folder or page it was suggested under was wrong',
	},
	reasonOutdated: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.outdated',
		defaultMessage: 'Outdated',
		description: 'Reason checkbox for a rejected suggestion: the content is out of date',
	},
	reasonDuplicate: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.duplicate',
		defaultMessage: 'Already in this space',
		description:
			'Reason checkbox for a rejected suggestion: the same information already exists in the space',
	},
	reasonShouldNotBeShared: {
		id: 'space-curation.suggestion-decision-feedback-modal.reason.should-not-be-shared',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "Shouldn't be shared here",
		description:
			'Reason checkbox for a rejected suggestion: the content is private or sensitive and should not be surfaced in the space',
	},
	skip: {
		id: 'space-curation.suggestion-decision-feedback-modal.skip',
		defaultMessage: 'Skip',
		description: 'Button that closes the suggestion feedback survey without sending answers',
	},
	submit: {
		id: 'space-curation.suggestion-decision-feedback-modal.submit',
		defaultMessage: 'Send feedback',
		description: 'Button that sends the answers in the suggestion feedback survey',
	},
});

const REASONS: Record<DecisionType, ReadonlyArray<{ value: string; label: MessageDescriptor }>> = {
	accepted: [
		{ value: 'relevant', label: i18n.reasonRelevant },
		{ value: 'useful', label: i18n.reasonUseful },
		{ value: 'goodLocation', label: i18n.reasonGoodLocation },
		{ value: 'upToDate', label: i18n.reasonUpToDate },
	],
	rejected: [
		{ value: 'notRelevant', label: i18n.reasonNotRelevant },
		{ value: 'wrongLocation', label: i18n.reasonWrongLocation },
		{ value: 'outdated', label: i18n.reasonOutdated },
		{ value: 'duplicate', label: i18n.reasonDuplicate },
		{ value: 'shouldNotBeShared', label: i18n.reasonShouldNotBeShared },
	],
};

const styles = cssMap({
	row: {
		paddingBlock: token('space.150'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
	},
	logo: {
		flexShrink: 0,
		display: 'flex',
	},
	title: {
		minWidth: '0',
	},
});

type SuggestionReasonRowProps = {
	suggestion: Suggestion;
	decision: DecisionType;
	selectedReasons: ReadonlySet<string>;
	onToggleReason: (suggestionId: string, reason: string) => void;
};

const SuggestionReasonRow = ({
	suggestion,
	decision,
	selectedReasons,
	onToggleReason,
}: SuggestionReasonRowProps) => {
	const { formatMessage } = useIntl();
	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) =>
			onToggleReason(suggestion.id, event.currentTarget.value),
		[onToggleReason, suggestion.id],
	);

	return (
		<Box
			xcss={styles.row}
			role="group"
			aria-label={formatMessage(
				decision === 'accepted' ? i18n.acceptedGroupLabel : i18n.rejectedGroupLabel,
				{ title: suggestion.title },
			)}
			testId={`suggestion-decision-feedback-row-${suggestion.id}`}
		>
			<Stack space="space.100">
				<Flex alignItems="center" gap="space.100">
					<Box xcss={styles.logo} aria-hidden="true">
						<ProductLogo
							logoKey={getSuggestionLogoKeyFromContentUrl(suggestion.url)}
							size="small"
						/>
					</Box>
					<Box xcss={styles.title}>
						<Text weight="medium" maxLines={1}>
							{suggestion.title}
						</Text>
					</Box>
				</Flex>
				<Inline space="space.200" rowSpace="space.050" shouldWrap>
					{REASONS[decision].map((reason) => (
						<Checkbox
							key={reason.value}
							value={reason.value}
							label={formatMessage(reason.label)}
							isChecked={selectedReasons.has(reason.value)}
							onChange={handleChange}
						/>
					))}
				</Inline>
			</Stack>
		</Box>
	);
};

type SuggestionDecisionFeedbackModalProps = {
	decision: SuggestionDecision;
	spaceId?: string;
	spaceKey?: string;
	onClose: () => void;
};

export const SuggestionDecisionFeedbackModal = ({
	decision,
	spaceId,
	spaceKey,
	onClose,
}: SuggestionDecisionFeedbackModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [reasonsBySuggestionId, setReasonsBySuggestionId] = useState<
		ReadonlyMap<string, ReadonlySet<string>>
	>(() => new Map());
	const { accepted, rejected, trigger } = decision;
	const hasSelections = reasonsBySuggestionId.size > 0;

	const mountValuesRef = useRef({ spaceId, spaceKey, decision, createAnalyticsEvent });
	useEffect(() => {
		const {
			spaceId: si,
			spaceKey: sk,
			decision: d,
			createAnalyticsEvent: cae,
		} = mountValuesRef.current;
		cae({
			type: 'sendScreenEvent',
			data: {
				name: 'suggestionFeedbackModal',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					trigger: d.trigger,
					acceptedCount: d.accepted.length,
					rejectedCount: d.rejected.length,
				},
			},
		}).fire();
	}, []);

	const handleToggleReason = useCallback((suggestionId: string, reason: string) => {
		setReasonsBySuggestionId((prev) => {
			const reasons = new Set(prev.get(suggestionId));
			if (reasons.has(reason)) {
				reasons.delete(reason);
			} else {
				reasons.add(reason);
			}
			const next = new Map(prev);
			if (reasons.size > 0) {
				next.set(suggestionId, reasons);
			} else {
				next.delete(suggestionId);
			}
			return next;
		});
	}, []);

	const handleSkip = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'suggestionFeedbackModal',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					trigger,
					hadSelections: hasSelections,
				},
			},
		}).fire();
		onClose();
	};

	const handleSubmit = () => {
		const toAnsweredPayloads = (suggestions: Suggestion[], decisionType: DecisionType) =>
			suggestions.flatMap((s) => {
				const reasons = reasonsBySuggestionId.get(s.id);
				return reasons
					? [
							{
								suggestionId: s.id,
								sourceType: getSuggestionProductKeyFromContentUrl(s.url),
								providerId: s.providerId,
								decision: decisionType,
								reasons: Array.from(reasons),
							},
						]
					: [];
			});
		const feedback = toAnsweredPayloads(accepted, 'accepted').concat(
			toAnsweredPayloads(rejected, 'rejected'),
		);

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'submitted',
				actionSubject: 'suggestionFeedback',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					trigger,
					acceptedCount: accepted.length,
					rejectedCount: rejected.length,
					answeredCount: feedback.length,
					feedback,
				},
			},
		}).fire();
		onClose();
	};

	const renderSection = (
		suggestions: Suggestion[],
		decisionType: DecisionType,
		heading: MessageDescriptor,
	) =>
		suggestions.length > 0 && (
			<Stack space="space.050">
				<Heading size="xsmall" as="h3">
					{formatMessage(heading)}
				</Heading>
				<Stack>
					{suggestions.map((s) => (
						<SuggestionReasonRow
							key={s.id}
							suggestion={s}
							decision={decisionType}
							selectedReasons={reasonsBySuggestionId.get(s.id) ?? new Set()}
							onToggleReason={handleToggleReason}
						/>
					))}
				</Stack>
			</Stack>
		);

	return (
		<Modal onClose={handleSkip} width="medium">
			<ModalHeader>
				<Stack space="space.050">
					<ModalTitle>{formatMessage(i18n.title)}</ModalTitle>
					<Text color="color.text.subtle">{formatMessage(i18n.description)}</Text>
				</Stack>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.300">
					{renderSection(accepted, 'accepted', i18n.acceptedHeading)}
					{renderSection(rejected, 'rejected', i18n.rejectedHeading)}
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button appearance="subtle" onClick={handleSkip}>
					{formatMessage(i18n.skip)}
				</Button>
				<Button appearance="primary" onClick={handleSubmit} isDisabled={!hasSelections}>
					{formatMessage(i18n.submit)}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
